/**
 * Setup Builder — boots + board + bindings.
 * Self-contained step flow + matching engine. No build step, no external
 * requests except Shopify's own /cart/add.js. Boot/board/binding catalogs
 * and reference data are injected onto window by sections/setup-builder.liquid
 * and assets/setup-builder-data.js before this file runs.
 *
 * Flow: boot size → skill → terrain → lacing pref → entry pref → pick boots
 * → height/weight → pick board → binding colour pref → pick bindings →
 * bundle. Each "pick" step is a real selection (not an auto-attach) — later
 * steps match against the customer's actual picks, not just their raw
 * answers, since e.g. the Step On boot/binding compatibility check needs to
 * know the specific boot they chose, not just their typed boot size.
 */
(function () {
  "use strict";

  // Full step order. Which of these actually show up in a given run
  // depends on the "goal" answer (full setup / board only / boots only) —
  // see stepsForGoal below. "goal" itself is always first and always shown.
  var ALL_STEPS = [
    "goal", "boot-size", "skill", "terrain", "lacing", "entry", "pick-boots",
    "measurements", "pick-board", "binding-colour", "pick-binding", "bundle"
  ];

  // Bindings are only ever sold as part of a full setup (they need a
  // compatible boot AND board picked first), so "board only" and "boots
  // only" both drop the binding steps along with whichever of boot/board
  // isn't being bought. Boot size is kept even for "board only" since it
  // still drives the board's wide-fit check.
  var GOAL_STEP_NAMES = {
    full: ALL_STEPS,
    board: ["goal", "boot-size", "skill", "terrain", "measurements", "pick-board", "bundle"],
    boots: ["goal", "boot-size", "skill", "terrain", "lacing", "entry", "pick-boots", "bundle"]
  };

  function stepsForGoal(goal) {
    return GOAL_STEP_NAMES[goal] || ["goal"];
  }

  // Which in-progress picks a given step's answer feeds into. Used when
  // navigating back to an earlier step — those downstream picks were
  // computed off data that's now back up for revision, so they're cleared
  // rather than silently going stale.
  var STEP_INVALIDATES = {
    "goal": ["boot", "board", "binding"],
    "boot-size": ["boot", "board", "binding"],
    "skill": ["boot", "board", "binding"],
    "terrain": ["boot", "board", "binding"],
    "lacing": ["boot", "binding"],
    "entry": ["boot", "binding"],
    "pick-boots": ["binding"],
    "measurements": ["board", "binding"],
    "pick-board": ["binding"],
    "binding-colour": ["binding"],
    "pick-binding": []
  };

  // Which raw answers belong to a step that only shows up for certain
  // goals. Switching goal (e.g. "full setup" → "just boots") already clears
  // downstream picks via STEP_INVALIDATES, but the raw answers themselves
  // were sticking around even once their step no longer exists in the new
  // flow — height/weight and binding colour would keep showing in the "So
  // far" sidebar for a customer who'd switched to a goal that never asks
  // for them. Steps every goal shares (boot-size, skill, terrain) aren't
  // listed here since they never need clearing.
  var STEP_ANSWER_FIELDS = {
    lacing: ["lacingPref"],
    entry: ["entryPref"],
    measurements: ["heightCm", "weightKg"],
    "binding-colour": ["colourPref"]
  };

  // Measurements persistence — logged-in customers only (gated by Liquid
  // exposing customer.id, see sections/setup-builder.liquid), browser-local
  // via localStorage. Deliberately only boot/height/weight, never skill
  // level or terrain, since those are meant to be answered fresh each time.
  var CUSTOMER_ID = window.SetupBuilderCustomerId || null;
  var MEASUREMENTS_KEY = CUSTOMER_ID ? "setup_builder_measurements_" + CUSTOMER_ID : null;

  function loadSavedMeasurements() {
    if (!MEASUREMENTS_KEY) return null;
    try {
      var raw = localStorage.getItem(MEASUREMENTS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveMeasurements(answers) {
    if (!MEASUREMENTS_KEY) return;
    try {
      localStorage.setItem(MEASUREMENTS_KEY, JSON.stringify({
        bootSize: answers.bootSize,
        scale: answers.scale,
        heightCm: answers.heightCm,
        weightKg: answers.weightKg
      }));
    } catch (e) {
      // localStorage can throw (private browsing, storage full, etc.) —
      // saving is a nice-to-have, never worth breaking the flow over.
    }
  }

  function init(root) {
    var boots = (window.SetupBuilderBoots || []).filter(isUsableBoot);
    var boards = (window.SetupBuilderBoards || []).filter(isUsableBoard);
    var bindings = (window.SetupBuilderBindings || []).filter(isUsableBinding);
    var cfg = window.SetupBuilderConfig || { weightSizeTable: [], descriptors: {} };

    // Generated once per page load, not per render — stable for this
    // customer's whole visit (so refreshing/going back doesn't reshuffle
    // things underneath them), but different from visit to visit. Used only
    // to break ties between genuinely comparable candidates (see
    // scoreJitter below) — without it, two products tied on terrain/skill/
    // size score always resolve in fixed catalog order, so every customer
    // with similar answers was seeing the exact same picks.
    var sessionSeed = Math.random().toString(36).slice(2);

    var state = {
      // Gates the whole wizard behind a single "Start building" click —
      // the page copy above the builder already covers what it is/how it
      // works, so this is deliberately just a threshold, not a second
      // explanation, and isn't tracked as a step in its own right (no
      // stepper pill for it).
      started: false,
      stepIndex: 0,
      maxReached: 0,
      // Only the goal question until it's answered — the rest of the flow
      // is trimmed to match once we know what they're actually shopping for.
      steps: ["goal"],
      answers: {
        goal: null, // 'full' | 'board' | 'boots'
        bootSize: null,
        // Scratch value only — not itself submitted anywhere. Kept on
        // answers (rather than local to bootSizeStep) purely so it survives
        // the re-render when the Men's/Women's toggle is clicked, so the US
        // field stays in sync with the size they actually typed.
        mondoCm: null,
        // Live (not-yet-Continued) value of the US size field itself —
        // without this, any re-render triggered while typing (e.g. the
        // Men's/Women's toggle) would reset the field back to the last
        // *committed* bootSize, silently discarding whatever was typed.
        bootSizeDraft: null,
        scale: "mens", // 'mens' | 'womens'
        skillLevel: null, // beginner | intermediate | advanced
        terrain: [], // multi-select
        lacingPref: null, // boa | laces | speed lace | hybrid | none
        entryPref: null, // step on | traditional | none
        heightCm: null,
        weightKg: null,
        colourPref: null // black | white | grey | blue | red | green | none
      },
      picks: {
        boot: null,
        board: null,
        binding: null
      },
      // Ids already shown (and rejected via "Show different picks") per
      // category, so a refresh cycles to the next-best distinct set rather
      // than repeating the same 3. Reset whenever that category's pick (or
      // an answer upstream of it) changes, so a fresh visit starts clean.
      excludeIds: {
        boot: [],
        board: [],
        binding: []
      }
    };

    var saved = loadSavedMeasurements();
    if (saved) {
      if (saved.bootSize) state.answers.bootSize = saved.bootSize;
      if (saved.scale) state.answers.scale = saved.scale;
      if (saved.heightCm) state.answers.heightCm = saved.heightCm;
      if (saved.weightKg) state.answers.weightKg = saved.weightKg;
    }

    render();

    function goToStep(i) {
      state.stepIndex = i;
      // Editing an earlier step invalidates confirmed answers past it —
      // and any picks that were computed off them.
      if (i < state.maxReached) {
        state.maxReached = i;
        var invalidated = STEP_INVALIDATES[state.steps[i]] || [];
        invalidated.forEach(function (key) { state.picks[key] = null; state.excludeIds[key] = []; });
      }
      render();
    }

    // The goal question changes which steps exist at all (not just which
    // answer was given), so it gets its own confirm path that rebuilds
    // state.steps rather than reusing confirmStep's fixed-index-plus-one.
    function confirmGoal(goal) {
      state.answers.goal = goal;
      var newSteps = stepsForGoal(goal);
      Object.keys(STEP_ANSWER_FIELDS).forEach(function (step) {
        if (newSteps.indexOf(step) === -1) {
          STEP_ANSWER_FIELDS[step].forEach(function (field) { state.answers[field] = null; });
        }
      });
      state.steps = newSteps;
      state.maxReached = 1;
      state.stepIndex = 1;
      render();
    }

    function confirmStep(i, patch) {
      Object.assign(state.answers, patch);
      state.maxReached = Math.max(state.maxReached, i + 1);
      state.stepIndex = i + 1;
      saveMeasurements(state.answers);
      render();
    }

    function confirmPick(i, key, rec) {
      state.picks[key] = rec;
      state.excludeIds[key] = [];
      state.maxReached = Math.max(state.maxReached, i + 1);
      state.stepIndex = i + 1;
      render();
    }

    function render() {
      root.innerHTML = "";

      if (!state.started) {
        root.appendChild(renderWelcome());
        return;
      }

      var name = state.steps[state.stepIndex];
      // The goal question never gets the stepper or sidebar — nothing's
      // been answered yet at that point, so there's nothing for a summary
      // to show, and a stepper with its one lonely pill reads as broken UI
      // rather than progress. Same treatment as the welcome screen above.
      if (name === "goal") {
        root.appendChild(renderStep(name));
        return;
      }

      // Two-column layout: an optional answers sidebar on the left, the
      // stepper + current step on the right. The sidebar only appears once
      // there's something to show it, so the very first question (before
      // anything's answered) still renders as a single centered column.
      var layout = el("div", "sb-layout");
      var summary = renderAnswerSummary();
      if (summary) layout.appendChild(summary);

      var main = el("div", "sb-layout__main");
      main.appendChild(renderStepper());
      if (name === "bundle") {
        main.appendChild(renderBundle());
      } else {
        main.appendChild(renderStep(name));
      }
      layout.appendChild(main);
      root.appendChild(layout);
    }

    // A running "here's what you've told us" sidebar — grows as each
    // question gets answered, and only ever lists answers that are
    // actually set, so it looks right regardless of which goal (full setup
    // / board only / boots only) trimmed the flow down to.
    function renderAnswerSummary() {
      var a = state.answers;
      var parts = [];

      if (a.bootSize) {
        parts.push((a.scale === "womens" ? "Women's" : "Men's") + " " + a.bootSize + " US");
      }
      if (a.skillLevel) parts.push(capitalize(a.skillLevel));
      if (a.terrain && a.terrain.length) {
        parts.push(a.terrain.map(capitalize).join("/"));
      }
      if (a.lacingPref && a.lacingPref !== "none") parts.push(capitalize(a.lacingPref) + " lacing");
      if (a.entryPref && a.entryPref !== "none") {
        parts.push(a.entryPref === "step on" ? "Step On" : "Traditional entry");
      }
      if (a.heightCm && a.weightKg) parts.push(a.heightCm + "cm / " + a.weightKg + "kg");
      if (a.colourPref && a.colourPref !== "none") parts.push(capitalize(a.colourPref) + " bindings");

      if (!parts.length) return null;

      var wrap = el("div", "sb-answer-summary");
      wrap.appendChild(el("div", "sb-answer-summary__label", "So far"));
      var list = el("div", "sb-answer-summary__list");
      parts.forEach(function (p) {
        list.appendChild(el("div", "sb-answer-summary__item", p));
      });
      wrap.appendChild(list);
      return wrap;
    }

    function renderWelcome() {
      var panel = el("div", "sb-panel sb-welcome");
      panel.appendChild(el("h3", "sb-step__title", "Ready?"));
      var btn = el("button", "sb-btn sb-btn--primary", "Start building");
      btn.type = "button";
      btn.addEventListener("click", function () {
        state.started = true;
        render();
      });
      panel.appendChild(btn);
      return panel;
    }

    // Small numbered dots rather than full text pills — the pill-shaped,
    // bold-uppercase treatment used to be identical to the option-select
    // pills below it, which read as "another set of choices" rather than
    // "here's your progress." Dots are compact and visually distinct; the
    // step name is only spelled out once, as a caption under the track, for
    // whichever step is actually current.
    function renderStepper() {
      var wrap = el("div", "sb-stepper");
      var track = el("div", "sb-stepper__track");
      state.steps.forEach(function (name, i) {
        var done = i < state.maxReached;
        var current = i === state.stepIndex;
        var clickable = done && !current;
        var dot = el("button", "sb-stepper__dot" + (current ? " is-current" : "") + (done ? " is-done" : "") + (clickable ? " is-clickable" : ""), String(i + 1));
        dot.type = "button";
        // Visible content is just the step number — the full name is still
        // exposed to assistive tech via aria-label, same information,
        // without needing the horizontal space to spell it out per step.
        dot.setAttribute("aria-label", stepLabel(name));
        if (current) dot.setAttribute("aria-current", "step");
        if (clickable) {
          dot.addEventListener("click", function () { goToStep(i); });
        } else {
          // Real <button disabled> (not just a styled div) so keyboard and
          // screen-reader users can reach completed steps the same way
          // mouse/touch users already could, and future steps are
          // correctly announced as unavailable rather than silently inert.
          dot.disabled = true;
        }
        track.appendChild(dot);
      });
      wrap.appendChild(track);
      wrap.appendChild(el("div", "sb-stepper__label", stepLabel(state.steps[state.stepIndex])));
      return wrap;
    }

    function stepLabel(name) {
      return {
        goal: "What you need",
        "boot-size": "Boot size",
        skill: "Riding style",
        terrain: "Terrain",
        lacing: "Lacing",
        entry: "Entry system",
        "pick-boots": "Your boots",
        measurements: "Height & weight",
        "pick-board": "Your board",
        "binding-colour": "Binding colour",
        "pick-binding": "Your bindings",
        bundle: "Your bundle"
      }[name];
    }

    function renderStep(name) {
      // The narrow 480px sb-panel suits a single question/toggle/input, but
      // strangles a results grid down to one column — pick-* steps need the
      // full width so recommendation cards can sit side by side.
      var isPicker = name === "pick-boots" || name === "pick-board" || name === "pick-binding";
      var panel = el("div", isPicker ? "sb-panel sb-panel--wide" : "sb-panel");
      if (name === "goal") panel.appendChild(goalStep());
      if (name === "boot-size") panel.appendChild(bootSizeStep());
      if (name === "skill") panel.appendChild(skillStep());
      if (name === "terrain") panel.appendChild(terrainStep());
      if (name === "lacing") panel.appendChild(lacingStep());
      if (name === "entry") panel.appendChild(entryStep());
      if (name === "pick-boots") panel.appendChild(pickBootsStep());
      if (name === "measurements") panel.appendChild(measurementsStep());
      if (name === "pick-board") panel.appendChild(pickBoardStep());
      if (name === "binding-colour") panel.appendChild(bindingColourStep());
      if (name === "pick-binding") panel.appendChild(pickBindingStep());
      return panel;
    }

    // ---- Step: what are they shopping for ----------------------------
    function goalStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "What are you looking for?"));
      f.appendChild(el("p", "sb-step__hint", "Pick a full setup if you need everything, or just the piece you're after — we'll only ask what's relevant to that."));

      var options = [
        ["full", "Full setup"],
        ["board", "Just a snowboard"],
        ["boots", "Just boots"]
      ];
      var group = el("div", "sb-options");
      options.forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.goal === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () {
          confirmGoal(opt[0]);
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    // ---- Step: boot size + scale ------------------------------------
    function bootSizeStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "What's your boot size?"));
      f.appendChild(el("p", "sb-step__hint", bootSizeHint(state.answers.goal)));

      var toggle = el("div", "sb-toggle");
      ["mens", "womens"].forEach(function (scale) {
        var btn = el("button", "sb-toggle__btn" + (state.answers.scale === scale ? " is-active" : ""));
        btn.type = "button";
        btn.textContent = scale === "mens" ? "Men's" : "Women's";
        btn.addEventListener("click", function () {
          state.answers.scale = scale;
          render();
        });
        toggle.appendChild(btn);
      });
      f.appendChild(toggle);

      var input = el("input", "sb-input");
      input.type = "number";
      input.step = "0.5";
      input.min = "3";
      input.max = "16";
      input.placeholder = "US size, e.g. 9.5";
      // Priority: an on-record Mondo point wins (it's the more reliable
      // source), then whatever's been typed into this field but not yet
      // Continued past, then the last committed value. Without the draft
      // tier, any re-render triggered while typing — the Men's/Women's
      // toggle, most commonly — would silently discard an in-progress edit
      // and fall back to the stale committed bootSize.
      if (state.answers.mondoCm) {
        var recomputed = mondoToUs(state.answers.mondoCm, state.answers.scale);
        if (recomputed) input.value = recomputed;
      } else if (state.answers.bootSizeDraft != null) {
        input.value = state.answers.bootSizeDraft;
      } else if (state.answers.bootSize) {
        input.value = state.answers.bootSize;
      }
      input.addEventListener("input", function () {
        // Typing directly into this field means it's now the source of
        // truth — clear any on-record Mondo point so it stops silently
        // overwriting this on the next re-render.
        state.answers.mondoCm = null;
        state.answers.bootSizeDraft = input.value;
      });
      f.appendChild(input);

      f.appendChild(bootSizeMondoConverter(input));
      f.appendChild(bootSizeHelpDetails());

      var btnNext = el("button", "sb-btn sb-btn--primary", "Continue");
      btnNext.type = "button";
      btnNext.addEventListener("click", function () {
        var val = parseFloat(input.value);
        if (!val || val <= 0) {
          showError(f, "Enter a boot size to continue.");
          return;
        }
        confirmStep(state.steps.indexOf("boot-size"), { bootSize: val, scale: state.answers.scale });
      });
      f.appendChild(btnNext);
      return f;
    }

    // Mondo point (foot length in cm) is the more reliable way to find a
    // true boot size — this box lets a customer enter that instead and
    // auto-fills the US field, without forcing anyone who already knows
    // their US size to go through it.
    function bootSizeMondoConverter(usInput) {
      var wrap = el("div", "sb-mondo");
      wrap.appendChild(el("label", "sb-mondo__label", "Know your Mondo point (foot length in cm) instead? Enter it here and we'll convert it:"));

      var mondoInput = el("input", "sb-input sb-mondo__input");
      mondoInput.type = "number";
      mondoInput.step = "0.5";
      mondoInput.placeholder = "Mondo point, e.g. 27.5";
      if (state.answers.mondoCm) mondoInput.value = state.answers.mondoCm;

      mondoInput.addEventListener("input", function () {
        var mondo = parseFloat(mondoInput.value);
        if (!mondo) {
          state.answers.mondoCm = null;
          return;
        }
        state.answers.mondoCm = mondo;
        var us = mondoToUs(mondo, state.answers.scale);
        if (us) {
          usInput.value = us;
          state.answers.bootSizeDraft = us;
        }
      });

      wrap.appendChild(mondoInput);
      return wrap;
    }

    function bootSizeHelpDetails() {
      var details = document.createElement("details");
      details.className = "sb-boot-help";
      var summary = document.createElement("summary");
      summary.className = "sb-boot-help__summary";
      summary.textContent = "How do I find my Mondo point?";
      details.appendChild(summary);

      var body = el("div", "sb-boot-help__body");
      body.appendChild(el("p", "", "Stand on a piece of paper wearing the socks you'll actually ride in, mark the back of your heel and the tip of your longest toe, then measure the distance in centimetres — that's your Mondo point."));
      body.appendChild(el("p", "", "Measure both feet and use the larger number, and measure later in the day if you can — feet swell a little by the afternoon."));
      details.appendChild(body);
      return details;
    }

    function bootSizeHint(goal) {
      if (goal === "board") {
        return "We use this to work out whether your board needs a wide fit — bigger feet need a wider board to avoid toe/heel drag.";
      }
      if (goal === "boots") {
        return "This sizes your boots exactly. Sizing can vary slightly between brands, so if you're between sizes, it's worth trying a pair on in-store.";
      }
      return "This sizes your boots exactly, and decides whether your board needs a wide fit — bigger feet need a wider board to avoid toe/heel drag. Sizing can vary slightly between brands, so if you're between sizes, it's worth trying a pair on in-store.";
    }

    // This step only ever shows up for "full" or "boots" goals (the
    // "board" goal skips it entirely — see GOAL_STEP_NAMES), so it only
    // needs to know whether to promise board+binding compatibility or just
    // the boot itself. Promising "we'll keep your board and binding
    // compatible" to a boots-only customer, who never sees a board or
    // binding step at all, was misleading.
    function entryHint(goal) {
      if (goal === "boots") {
        return "Step On boots need Step On bindings — let us know your preference so it's on record if you already have (or plan to get) a compatible binding.";
      }
      return "Step On boots need Step On bindings — pick this now and we'll keep your board, boot and binding compatible all the way through.";
    }

    // Mondo point is just foot length in cm, and US boot size is linear
    // against it: US Men's = Mondo - 18, US Women's = Mondo - 17 (i.e.
    // Men's + 1). Verified against evo/REI/DIN Calculator's published
    // conversion tables across the whole 22-32cm range, not just the
    // "add the digits together" mnemonic some sizing guides use, which only
    // holds up below Mondo 30.
    function mondoToUs(mondoCm, scale) {
      var us = mondoCm - (scale === "womens" ? 17 : 18);
      us = Math.round(us * 2) / 2;
      return us > 0 ? us : null;
    }

    // ---- Step: skill level -----------------------------------------
    function skillStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Riding style / skill level"));
      f.appendChild(el("p", "sb-step__hint", "This shapes how forgiving or aggressive your gear is — beginner setups are softer and easier to control, advanced setups hold up at higher speeds and reward a harder push."));

      var options = [
        ["beginner", "Beginner"],
        ["intermediate", "Intermediate"],
        ["advanced", "Advanced"]
      ];
      var group = el("div", "sb-options");
      options.forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.skillLevel === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () {
          confirmStep(state.steps.indexOf("skill"), { skillLevel: opt[0] });
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    // ---- Step: terrain (multi-select) -------------------------------
    function terrainStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Where do you ride most?"));
      f.appendChild(el("p", "sb-step__hint", "Pick as many as apply. Pick “All-mountain” if you mostly do a bit of everything. This adjusts the length and flex we recommend — freeride and powder size up for stability and float, park sizes down for a playful, easy-to-throw-around feel."));

      var options = [
        ["all-mountain", "All-mountain"],
        ["park", "Park"],
        ["freeride", "Freeride"],
        ["powder", "Powder"],
        ["resort", "Resort"]
      ];
      var selected = state.answers.terrain.slice();
      var group = el("div", "sb-options");
      options.forEach(function (opt) {
        var btn = el("button", "sb-option" + (selected.indexOf(opt[0]) > -1 ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () {
          var idx = selected.indexOf(opt[0]);
          if (idx > -1) selected.splice(idx, 1);
          else selected.push(opt[0]);
          state.answers.terrain = selected;
          render();
        });
        group.appendChild(btn);
      });
      f.appendChild(group);

      var btnNext = el("button", "sb-btn sb-btn--primary", "Continue");
      btnNext.type = "button";
      btnNext.addEventListener("click", function () {
        if (!selected.length) {
          showError(f, "Pick at least one terrain to continue.");
          return;
        }
        confirmStep(state.steps.indexOf("terrain"), { terrain: selected });
      });
      f.appendChild(btnNext);
      return f;
    }

    // ---- Step: lacing preference -------------------------------------
    function lacingStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Any lacing preference for your boots?"));
      f.appendChild(el("p", "sb-step__hint", "Pick “No preference” if you're not fussed — we'll still match you on fit and riding style. This is a comfort choice only and won't change sizing."));

      var options = [
        ["boa", "Boa"],
        ["laces", "Laces"],
        ["speed lace", "Speed Lace"],
        ["hybrid", "Hybrid"],
        ["none", "No preference"]
      ];
      var group = el("div", "sb-options");
      options.forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.lacingPref === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () {
          confirmStep(state.steps.indexOf("lacing"), { lacingPref: opt[0] });
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    // ---- Step: entry system preference --------------------------------
    function entryStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Step On, or traditional strap-in?"));
      f.appendChild(el("p", "sb-step__hint", entryHint(state.answers.goal)));

      var options = [
        ["step on", "Step On"],
        ["traditional", "Traditional"],
        ["none", "No preference"]
      ];
      var group = el("div", "sb-options");
      options.forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.entryPref === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () {
          confirmStep(state.steps.indexOf("entry"), { entryPref: opt[0] });
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    // ---- Step: pick boots ---------------------------------------------
    function pickBootsStep() {
      var match = matchBoots(boots, state.answers, cfg, state.excludeIds.boot, sessionSeed);
      // Refreshing enough times can exhaust the pool — wrap back around to
      // the top-ranked picks rather than leaving the customer with nothing.
      if (!match.recommendations.length && state.excludeIds.boot.length) {
        state.excludeIds.boot = [];
        match = matchBoots(boots, state.answers, cfg, state.excludeIds.boot, sessionSeed);
      }
      return renderPicks(
        "Pick your boots",
        match,
        "boot",
        state.steps.indexOf("pick-boots"),
        buildBootCardBody,
        "We couldn't find boots in this size yet — give the store a shout and we'll help directly.",
        "Nothing in this combination is in stock right now — give the store a shout and we'll help directly. Here's what we'd put you in once it's back:"
      );
    }

    // ---- Step: height + weight ------------------------------------
    function measurementsStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Height & weight"));
      f.appendChild(el("p", "sb-step__hint", "Weight sets your base board length; height fine-tunes it when you're right on the boundary between two sizes."));

      var heightInput = el("input", "sb-input");
      heightInput.type = "number";
      heightInput.placeholder = "Height (cm)";
      if (state.answers.heightCm) heightInput.value = state.answers.heightCm;

      var weightInput = el("input", "sb-input");
      weightInput.type = "number";
      weightInput.placeholder = "Weight (kg)";
      if (state.answers.weightKg) weightInput.value = state.answers.weightKg;

      f.appendChild(heightInput);
      f.appendChild(weightInput);

      var btnNext = el("button", "sb-btn sb-btn--primary", "Continue");
      btnNext.type = "button";
      btnNext.addEventListener("click", function () {
        var h = parseFloat(heightInput.value);
        var w = parseFloat(weightInput.value);
        if (!h || !w) {
          showError(f, "Enter both height and weight to continue.");
          return;
        }
        confirmStep(state.steps.indexOf("measurements"), { heightCm: h, weightKg: w });
      });
      f.appendChild(btnNext);
      return f;
    }

    // ---- Step: pick board -----------------------------------------------
    function pickBoardStep() {
      var match = matchBoards(boards, state.answers, cfg, state.excludeIds.board, sessionSeed);
      if (!match.recommendations.length && state.excludeIds.board.length) {
        state.excludeIds.board = [];
        match = matchBoards(boards, state.answers, cfg, state.excludeIds.board, sessionSeed);
      }
      return renderPicks(
        "Pick your board",
        match,
        "board",
        state.steps.indexOf("pick-board"),
        buildBoardCardBody,
        "We couldn't find a board for this combination yet — give the store a shout and we'll help directly.",
        "Nothing in this combination is in stock right now — give the store a shout and we'll help directly. Here's what we'd put you on once it's back:"
      );
    }

    // ---- Step: binding colour preference -------------------------------
    function bindingColourStep() {
      var f = el("div", "sb-step");
      f.appendChild(el("h3", "sb-step__title", "Any colour preference for your bindings?"));
      f.appendChild(el("p", "sb-step__hint", "We use this as a tiebreaker only — fit and riding style always come first."));

      var options = [
        ["black", "Black"],
        ["white", "White"],
        ["grey", "Grey"],
        ["blue", "Blue"],
        ["red", "Red"],
        ["green", "Green"],
        ["none", "No preference"]
      ];
      var group = el("div", "sb-options");
      options.forEach(function (opt) {
        var btn = el("button", "sb-option" + (state.answers.colourPref === opt[0] ? " is-active" : ""), opt[1]);
        btn.type = "button";
        btn.addEventListener("click", function () {
          confirmStep(state.steps.indexOf("binding-colour"), { colourPref: opt[0] });
        });
        group.appendChild(btn);
      });
      f.appendChild(group);
      return f;
    }

    // ---- Step: pick binding ----------------------------------------------
    function pickBindingStep() {
      var match = matchBindings(bindings, state.answers, state.picks, cfg, state.excludeIds.binding, sessionSeed);
      if (!match.recommendations.length && state.excludeIds.binding.length) {
        state.excludeIds.binding = [];
        match = matchBindings(bindings, state.answers, state.picks, cfg, state.excludeIds.binding, sessionSeed);
      }
      return renderPicks(
        "Pick your bindings",
        match,
        "binding",
        state.steps.indexOf("pick-binding"),
        buildBindingCardBody,
        "We couldn't find bindings for this combination yet — give the store a shout and we'll help directly.",
        "Nothing in this combination is in stock right now — give the store a shout and we'll help directly. Here's what we'd put you on once it's back:"
      );
    }

    // ---- Shared "pick one of N" step renderer ----------------------------
    function renderPicks(title, match, pickKey, stepIdx, buildCardBody, emptyMessage, outOfStockMessage) {
      var wrap = el("div", "sb-step sb-step--picker");

      var titleRow = el("div", "sb-step__title-row");
      titleRow.appendChild(el("h3", "sb-step__title", title));
      wrap.appendChild(titleRow);

      if (match.banner) wrap.appendChild(el("div", "sb-banner", match.banner));

      if (!match.recommendations.length) {
        wrap.appendChild(el("p", "sb-empty", emptyMessage));
        return wrap;
      }

      if (match.outOfStockOnly) {
        wrap.appendChild(el("p", "sb-empty", outOfStockMessage));
      }

      var grid = el("div", "sb-grid");
      match.recommendations.forEach(function (rec) {
        var card = el("div", "sb-card" + (rec.flags.outOfStock ? " sb-card--out-of-stock" : ""));
        buildCardBody(card, rec);
        if (!rec.flags.outOfStock) {
          var btn = el("button", "sb-btn sb-btn--primary", "Select these");
          btn.type = "button";
          btn.addEventListener("click", function () {
            confirmPick(stepIdx, pickKey, rec);
          });
          card.appendChild(btn);
        }
        grid.appendChild(card);
      });
      wrap.appendChild(grid);

      // Refreshing doesn't make sense against an out-of-stock reference
      // list (there's nothing else to swap in for "currently unavailable"),
      // so it's only offered on a normal, in-stock result set.
      if (!match.outOfStockOnly) {
        var refreshBtn = el("button", "sb-btn sb-btn--secondary sb-refresh-btn", "Show different picks");
        refreshBtn.type = "button";
        refreshBtn.addEventListener("click", function () {
          var shownIds = match.recommendations.map(function (rec) { return rec[pickKey].id; });
          state.excludeIds[pickKey] = state.excludeIds[pickKey].concat(shownIds);
          render();
        });
        wrap.appendChild(refreshBtn);
      }

      return wrap;
    }

    // ---- Card bodies (image/title/badges/reasoning per product type) -----
    function buildBootCardBody(card, rec) {
      var boot = rec.boot;
      var link = document.createElement("a");
      link.className = "sb-card__link";
      link.href = boot.url || "#";
      link.target = "_blank";
      link.rel = "noopener";
      if (boot.image) {
        var img = document.createElement("img");
        img.className = "sb-card__image";
        img.src = boot.image;
        img.alt = boot.title;
        link.appendChild(img);
      }
      link.appendChild(el("h4", "sb-card__title", boot.title));
      card.appendChild(link);
      card.appendChild(el("div", "sb-card__length", "US " + rec.variant.bootSizeUs));

      if (rec.flags.outOfStock) card.appendChild(el("span", "sb-badge sb-badge--out-of-stock", "Out of stock — call to check availability"));
      if (rec.flags.lineFallback) card.appendChild(el("span", "sb-badge sb-badge--fallback", "Outside your usual size range"));
      if (rec.flags.poorStyleFit) card.appendChild(el("span", "sb-badge sb-badge--fallback", "Best available for your style"));

      var reasoning = el("div", "sb-card__reasoning");
      rec.reasoning.forEach(function (sentence) { reasoning.appendChild(el("p", "", sentence)); });
      card.appendChild(reasoning);
    }

    function buildBoardCardBody(card, rec) {
      var board = rec.board;
      var link = document.createElement("a");
      link.className = "sb-card__link";
      link.href = board.url || "#";
      link.target = "_blank";
      link.rel = "noopener";
      if (board.image) {
        var img = document.createElement("img");
        img.className = "sb-card__image";
        img.src = board.image;
        img.alt = board.title;
        link.appendChild(img);
      }
      link.appendChild(el("h4", "sb-card__title", board.title));
      card.appendChild(link);
      card.appendChild(el("div", "sb-card__length", rec.variant.lengthCm + "cm"));

      if (rec.flags.outOfStock) card.appendChild(el("span", "sb-badge sb-badge--out-of-stock", "Out of stock — call to check availability"));
      if (rec.flags.lineFallback) card.appendChild(el("span", "sb-badge sb-badge--fallback", "Outside your usual size range"));
      if (rec.flags.closestAvailable) card.appendChild(el("span", "sb-badge sb-badge--fallback", "Closest available match"));
      if (rec.flags.poorStyleFit) card.appendChild(el("span", "sb-badge sb-badge--fallback", "Best available for your style"));

      var reasoning = el("div", "sb-card__reasoning");
      rec.reasoning.forEach(function (sentence) { reasoning.appendChild(el("p", "", sentence)); });
      card.appendChild(reasoning);
    }

    function buildBindingCardBody(card, rec) {
      var binding = rec.binding;
      var link = document.createElement("a");
      link.className = "sb-card__link";
      link.href = binding.url || "#";
      link.target = "_blank";
      link.rel = "noopener";
      if (binding.image) {
        var img = document.createElement("img");
        img.className = "sb-card__image";
        img.src = binding.image;
        img.alt = binding.title;
        link.appendChild(img);
      }
      link.appendChild(el("h4", "sb-card__title", binding.title));
      card.appendChild(link);
      card.appendChild(el("div", "sb-card__length", rec.variant.title));

      if (rec.flags.outOfStock) card.appendChild(el("span", "sb-badge sb-badge--out-of-stock", "Out of stock — call to check availability"));
      if (rec.flags.lineFallback) card.appendChild(el("span", "sb-badge sb-badge--fallback", "Outside your usual range"));
      if (rec.flags.poorStyleFit) card.appendChild(el("span", "sb-badge sb-badge--fallback", "Best available for your style"));

      var reasoning = el("div", "sb-card__reasoning");
      rec.reasoning.forEach(function (sentence) { reasoning.appendChild(el("p", "", sentence)); });
      card.appendChild(reasoning);
    }

    // ---- Bundle (final summary) -----------------------------------------
    function renderBundle() {
      var wrap = el("div", "sb-results sb-bundle");
      wrap.appendChild(el("h3", "sb-step__title", "Your bundle"));

      var items = [
        { rec: state.picks.boot, product: state.picks.boot && state.picks.boot.boot },
        { rec: state.picks.board, product: state.picks.board && state.picks.board.board },
        { rec: state.picks.binding, product: state.picks.binding && state.picks.binding.binding }
      ].filter(function (item) { return item.rec; });

      if (!items.length) {
        wrap.appendChild(el("p", "sb-empty", "Nothing picked yet."));
        return wrap;
      }

      var total = 0;
      var grid = el("div", "sb-grid");
      items.forEach(function (item) {
        var product = item.product;
        var variant = item.rec.variant;
        var card = el("div", "sb-card");

        var link = document.createElement("a");
        link.className = "sb-card__link";
        link.href = product.url || "#";
        link.target = "_blank";
        link.rel = "noopener";
        if (product.image) {
          var img = document.createElement("img");
          img.className = "sb-card__image";
          img.src = product.image;
          img.alt = product.title;
          link.appendChild(img);
        }
        link.appendChild(el("h4", "sb-card__title", product.title));
        card.appendChild(link);
        card.appendChild(el("div", "sb-card__length", variant.title || ""));

        var price = parseFloat(variant.price) || 0;
        total += price;
        card.appendChild(el("div", "sb-card__price", "$" + price.toFixed(2)));

        var addBtn = el("button", "sb-btn sb-btn--secondary", "Add to cart");
        addBtn.type = "button";
        addBtn.addEventListener("click", function () {
          addToCart(variant.id, addBtn);
        });
        card.appendChild(addBtn);

        grid.appendChild(card);
      });
      wrap.appendChild(grid);

      var addAllBtn = el("button", "sb-btn sb-btn--primary sb-bundle__add-all", "Add all to cart — $" + total.toFixed(2));
      addAllBtn.type = "button";
      addAllBtn.addEventListener("click", function () {
        var variantIds = items.map(function (item) { return item.rec.variant.id; });
        addAllToCart(variantIds, addAllBtn);
      });
      wrap.appendChild(addAllBtn);

      return wrap;
    }

    function showError(container, message) {
      var existing = container.querySelector(".sb-error");
      if (existing) existing.remove();
      container.appendChild(el("p", "sb-error", message));
    }
  }

  // ---- Cart -------------------------------------------------------------
  // Themes running CartJS (cartjs.co) drive their own cart drawer/count off
  // CartJS.addItem specifically — a plain fetch to /cart/add.js adds the
  // item but leaves their UI stale. addItem queues the request
  // asynchronously, so use its own success/error callbacks rather than
  // guessing at a fixed delay before checking /cart.js — the queue can take
  // longer than any fixed wait, which was producing false "couldn't add"
  // failures on items that had actually been added.
  function addItemToCart(variantId, callback) {
    function settle(ok) {
      if (ok) {
        document.dispatchEvent(new CustomEvent("setup-builder:added-to-cart", { detail: { variantId: variantId } }));
      }
      callback(ok);
    }

    if (window.CartJS && typeof window.CartJS.addItem === "function") {
      window.CartJS.addItem(variantId, 1, {}, {
        success: function () { settle(true); },
        error: function () { settle(false); }
      });
    } else {
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Cart add failed");
          return res.json();
        })
        .then(function () { settle(true); })
        .catch(function () { settle(false); });
    }
  }

  function addToCart(variantId, button) {
    var original = button.textContent;
    var settled = false;
    button.disabled = true;
    button.textContent = "Adding…";

    addItemToCart(variantId, function (ok) {
      settled = true;
      button.textContent = ok ? "Added ✓" : "Couldn't add — try again";
      if (!ok) button.disabled = false;
    });

    setTimeout(function () {
      if (settled) return;
      button.disabled = false;
      button.textContent = original;
    }, 4000);
  }

  function addAllToCart(variantIds, button) {
    button.disabled = true;
    button.textContent = "Adding…";
    var idx = 0;
    var anyFail = false;

    function next() {
      if (idx >= variantIds.length) {
        button.textContent = anyFail ? "Added most — check your cart" : "All added ✓";
        button.disabled = !anyFail;
        return;
      }
      var id = variantIds[idx++];
      addItemToCart(id, function (ok) {
        if (!ok) anyFail = true;
        next();
      });
    }
    next();
  }

  // ---- Matching engine: boots ---------------------------------------------
  function matchBoots(boots, answers, cfg, excludeIds, sessionSeed) {
    excludeIds = excludeIds || [];
    function notExcluded(rec) { return excludeIds.indexOf(rec.boot.id) === -1; }

    var dominantTerrain = terrainStyleAdjustment(answers.terrain, answers.skillLevel).dominant;
    var preferredLine = answers.scale === "womens" ? "womens" : "mens";

    var criteria = {
      bootSize: answers.bootSize,
      dominantTerrain: dominantTerrain,
      skillLevel: answers.skillLevel,
      lacingPref: answers.lacingPref,
      entryPref: answers.entryPref,
      sessionSeed: sessionSeed
    };

    // Unlike boards/bindings, boots never fall back to the opposite gender
    // pool — the same numeric size means a different actual foot size in
    // men's vs women's boot sizing, so a "Mens 10" customer shown a
    // "Womens 10" boot isn't a stylistic compromise, it's the wrong size.
    var primaryPool = boots.filter(function (b) {
      return b.product_line === preferredLine || b.product_line === "unisex";
    });

    var recs = pickBootCandidates(primaryPool, criteria, false).filter(notExcluded);

    var outOfStockOnly = false;
    if (!recs.length) {
      // notExcluded still applies here — without it, a customer who
      // clicks "Show different picks" until the real pool runs dry would
      // see the exact same, already-rejected boots come back mislabeled
      // as "out of stock" (they're not), and the outer wrap-around in
      // pickBootsStep would never trigger since this fallback always
      // finds *something*. Filtering here lets this pass come back
      // genuinely empty when it should, so the real wrap-around can run.
      outOfStockOnly = true;
      var primaryNoStock = pickBootCandidates(primaryPool, criteria, false, true).filter(notExcluded);
      recs = dedupeRecs(primaryNoStock, function (r) { return r.boot.id; }).slice(0, 3);
      recs.forEach(function (rec) { rec.flags.outOfStock = true; });
    }

    recs = recs.slice(0, 3).map(function (rec) {
      rec.flags.lineFallback = false;
      rec.reasoning = buildBootReasoning(rec, answers, cfg.descriptors || {});
      return rec;
    });

    return { recommendations: recs, banner: null, outOfStockOnly: outOfStockOnly };
  }

  function pickBootCandidates(pool, criteria, isFallbackPool, ignoreStock) {
    var scored = [];

    // Entry system is a hard filter, not a scoring preference — the Entry
    // System question explicitly promises "we'll keep your board, boot and
    // binding compatible all the way through", and a Step On boot shown
    // after picking Traditional (or vice versa) breaks that promise even if
    // it's technically still the best-scoring option otherwise.
    var hasEntryPref = criteria.entryPref && criteria.entryPref !== "none";

    pool.forEach(function (boot) {
      if (hasEntryPref && boot.entry_system !== criteria.entryPref) return;

      // Boots don't have a size range like boards/bindings do — each
      // variant IS a specific US size, so this is an exact match or
      // nothing, no "closest available" fallback the way board length has.
      var eligible = boot.variants.filter(function (v) {
        return (ignoreStock || v.available) && typeof v.bootSizeUs === "number" && v.bootSizeUs === criteria.bootSize;
      });
      if (!eligible.length) return;
      var variant = eligible[0];

      var terrainMatch = boot.terrain.indexOf(criteria.dominantTerrain) > -1 ? 0
        : (boot.terrain.indexOf("all-mountain") > -1 ? 1 : 2);
      var skillMatch = boot.skill_level === criteria.skillLevel ? 0 : (skillAdjacent(boot.skill_level, criteria.skillLevel) ? 1 : 2);

      var hasLacingPref = criteria.lacingPref && criteria.lacingPref !== "none";
      var lacingMatch = !hasLacingPref ? 1 : (boot.lacing_system === criteria.lacingPref ? 0 : 2);

      var poorStyleFit = terrainMatch === 2 || skillMatch === 2;
      var score = terrainMatch * 30 + skillMatch * 20 + lacingMatch * 10 + (isFallbackPool ? 500 : 0)
        + scoreJitter(criteria.sessionSeed, boot.id);

      scored.push({
        boot: boot,
        variant: variant,
        score: score,
        flags: { lineFallback: false, poorStyleFit: poorStyleFit, outOfStock: false }
      });
    });

    scored.sort(function (a, b) { return a.score - b.score; });
    return dedupeRecs(scored, function (r) { return r.boot.id; });
  }

  // Mirrors buildReasoning's approach for boards: a handful of phrasing
  // variants per sentence, picked deterministically off the boot's own id
  // (not Math.random) so the same boot reads the same way on repeat visits,
  // but boots shown side by side in one result set don't all read as the
  // same template with nouns swapped in.
  function buildBootReasoning(rec, answers, descriptors) {
    var boot = rec.boot;
    var sentences = [];

    var sizeOpeners = [
      "US {size} in your size.",
      "This comes in your US {size}.",
      "We've got it in your US {size}.",
      "Sized to US {size}, right in your range."
    ];
    sentences.push(pickVariant(sizeOpeners, boot, "sizeOpener").replace("{size}", rec.variant.bootSizeUs));

    // Real per-boot flavor text (Liquid pulls this from the product's own
    // description — see sections/setup-builder.liquid) so cards read as
    // distinct boots rather than the same template with numbers swapped.
    if (boot.description_blurb) {
      sentences.push(boot.description_blurb);
    }

    if (boot.lacing_system) {
      var lacingDesc = (descriptors.lacing_system || {})[boot.lacing_system] || (capitalize(boot.lacing_system) + " lacing");
      var lacingTemplates = [
        "It's got {lacing}.",
        "Lacing-wise, it's got {lacing}.",
        "It's fitted with {lacing}.",
        "For lacing, you're getting {lacing}."
      ];
      sentences.push(pickVariant(lacingTemplates, boot, "lacing").replace("{lacing}", lacingDesc));
    }

    if (boot.entry_system) {
      var entryDesc = (descriptors.entry_system || {})[boot.entry_system];
      if (entryDesc) sentences.push(capitalize(entryDesc) + ".");
    }

    // Describe the BOOT's own actual terrain/skill tags, not the customer's
    // requested criteria — every recommended boot shares the same criteria,
    // so using criteria values made this sentence read identically across
    // an entire results set regardless of how different the boots actually are.
    var terrainWords = (boot.terrain || [])
      .map(function (t) { return (descriptors.terrain || {})[t]; })
      .filter(Boolean);
    var terrainWord = joinNatural(terrainWords);
    var skillWord = (descriptors.skill_level || {})[boot.skill_level];
    if (terrainWord && skillWord) {
      var both = [
        "It's built for {terrain}, and suits riders who are {skill}.",
        "It's built for {terrain} — good for riders who are {skill}.",
        "This one's built for {terrain}, and works well for riders who are {skill}.",
        "It's made for {terrain}, and a solid pick for riders who are {skill}."
      ];
      sentences.push(pickVariant(both, boot, "terrainskill").replace("{terrain}", terrainWord).replace("{skill}", skillWord));
    } else if (terrainWord) {
      var terrainOnly = [
        "It's built for {terrain}.",
        "This one's designed for {terrain}.",
        "Built with {terrain} riding in mind."
      ];
      sentences.push(pickVariant(terrainOnly, boot, "terrainonly").replace("{terrain}", terrainWord));
    }

    if (typeof boot.flex_rating === "number") {
      var flexTemplates = [
        "Flex sits at {flex}/10.",
        "It's a {flex}/10 on the flex scale.",
        "Flex rating: {flex}/10.",
        "On flex, it comes in at {flex}/10."
      ];
      sentences.push(pickVariant(flexTemplates, boot, "flex").replace("{flex}", boot.flex_rating));
    }

    if (rec.flags.lineFallback) {
      var lineWord = (descriptors.product_line || {})[boot.product_line] || boot.product_line;
      sentences.push("These are a " + lineWord + " boot — we've widened the search here since nothing in your usual range fit well.");
    }
    if (rec.flags.poorStyleFit) {
      sentences.push("Not a close match for your terrain or skill picks, but the best fit we have in your size.");
    }

    return sentences;
  }

  // ---- Matching engine: boards ----------------------------------------------
  function matchBoards(boards, answers, cfg, excludeIds, sessionSeed) {
    excludeIds = excludeIds || [];
    function notExcluded(rec) { return excludeIds.indexOf(rec.board.id) === -1; }

    var flags = { banner: null };

    var sizeResult = computeBaseLength(answers.weightKg, answers.heightCm, cfg.weightSizeTable);
    var styleAdj = terrainStyleAdjustment(answers.terrain, answers.skillLevel);
    var targetLength = sizeResult.baseLength + styleAdj.adj;

    var outOfRange = answers.weightKg < 27 || answers.weightKg > 110;
    var heightMismatch = heightWeightMismatch(answers.heightCm, answers.weightKg);
    if (outOfRange || heightMismatch) {
      flags.banner = "This combination is outside our standard sizing range — contact the store and we'll help you get the right fit.";
    }

    // Same 1.0 offset as mondoToUs (Women's = Men's + 1) — this used to be
    // a hardcoded 1.5 here, disagreeing with the verified Mondo point
    // conversion used elsewhere, which could flip the wide-fit decision
    // right at the boundary.
    var mensEquivalentBoot = answers.scale === "womens" ? answers.bootSize - 1 : answers.bootSize;
    var requireWide = mensEquivalentBoot >= 10.5;

    var preferredLine = answers.scale === "womens" ? "womens" : "mens";
    var oppositeLine = preferredLine === "mens" ? "womens" : "mens";

    var criteria = {
      targetLength: targetLength,
      weightKg: answers.weightKg,
      requireWide: requireWide,
      skillLevel: answers.skillLevel,
      terrain: answers.terrain,
      dominantTerrain: styleAdj.dominant,
      sessionSeed: sessionSeed
    };

    var primaryPool = boards.filter(function (b) {
      return b.product_line === preferredLine || b.product_line === "unisex";
    });
    var fallbackPool = boards.filter(function (b) {
      return b.product_line === oppositeLine;
    });

    var recs = pickCandidates(primaryPool, criteria, false).filter(notExcluded);
    var usedFallbackLine = false;
    if (recs.length < 2) {
      var more = pickCandidates(fallbackPool, criteria, true).filter(notExcluded);
      if (more.length) usedFallbackLine = true;
      recs = dedupeRecs(recs.concat(more), function (r) { return r.board.id; }).slice(0, 3);
    }

    // Nothing in stock at all for this combination — rather than leave the
    // customer with nothing, show up to 3 boards we'd otherwise have
    // recommended, purely as a reference for when they call the store.
    // Re-run both pools ignoring stock; every result here is guaranteed
    // out of stock, since the stock-required pass above already came back
    // empty across the whole catalog for this criteria.
    var outOfStockOnly = false;
    if (!recs.length) {
      // notExcluded still applies here — see the matching comment in
      // matchBoots — otherwise a fully "Show different picks"-exhausted
      // pool comes back showing the same already-rejected boards
      // mislabeled as out of stock instead of properly wrapping around.
      outOfStockOnly = true;
      var primaryNoStock = pickCandidates(primaryPool, criteria, false, true).filter(notExcluded);
      var fallbackNoStock = pickCandidates(fallbackPool, criteria, true, true).filter(notExcluded);
      recs = dedupeRecs(primaryNoStock.concat(fallbackNoStock), function (r) { return r.board.id; }).slice(0, 3);
      recs.forEach(function (rec) { rec.flags.outOfStock = true; });
    }

    recs = recs.slice(0, 3).map(function (rec) {
      rec.flags.lineFallback = usedFallbackLine && rec.board.product_line === oppositeLine;
      rec.reasoning = buildReasoning(rec, criteria, sizeResult, styleAdj, cfg.descriptors || {});
      return rec;
    });

    return { recommendations: recs, banner: flags.banner, outOfStockOnly: outOfStockOnly };
  }

  function pickCandidates(pool, criteria, isFallbackPool, ignoreStock) {
    var scored = [];

    pool.forEach(function (board) {
      var eligibleVariants = board.variants.filter(function (v) {
        var widthOk = criteria.requireWide ? v.widthCategory === "wide" : v.widthCategory !== "wide";
        return widthOk;
      });
      if (!eligibleVariants.length) return;

      // ignoreStock is only ever set by the "nothing in stock" fallback
      // pass in matchBoards, once a normal stock-required pass has already
      // come back empty — so here it means "score against every eligible
      // variant regardless of stock" rather than widening the length match.
      var available = eligibleVariants.filter(function (v) { return ignoreStock || v.available; });
      if (!available.length) {
        return;
      }

      // Prefer a variant whose real weight_min/weight_max range actually
      // contains the customer's weight — that's the board's own engineered
      // recommendation, more authoritative than our generic store-wide
      // estimate. Only fall back to closest-by-length when a board has no
      // weight data yet, or none of its ranges cover this customer.
      var weightFit = available.filter(function (v) {
        return typeof v.weightMinKg === "number" && typeof v.weightMaxKg === "number" &&
          criteria.weightKg >= v.weightMinKg && criteria.weightKg <= v.weightMaxKg;
      });
      var usedWeightFit = weightFit.length > 0;
      var pool2 = usedWeightFit ? weightFit : available;

      // Among weight-valid variants, the board's own range is the more
      // authoritative signal — pick whichever range best centers the
      // customer's actual weight, not whichever length happens to be
      // closest to the generic chart's guess. Only fall back to
      // chart-distance when no board has real weight data to lean on.
      var best = usedWeightFit
        ? pool2.reduce(function (a, b) {
            var aCenter = (a.weightMinKg + a.weightMaxKg) / 2;
            var bCenter = (b.weightMinKg + b.weightMaxKg) / 2;
            return Math.abs(criteria.weightKg - aCenter) <= Math.abs(criteria.weightKg - bCenter) ? a : b;
          })
        : pool2.reduce(function (a, b) {
            return Math.abs(a.lengthCm - criteria.targetLength) <= Math.abs(b.lengthCm - criteria.targetLength) ? a : b;
          });

      var lengthDiff = Math.abs(best.lengthCm - criteria.targetLength);
      // A real weight-range match is confident even if it diverges from our
      // generic estimate — only flag "closest available" when we had to
      // fall back to length-distance guessing.
      var closestAvailable = !usedWeightFit && lengthDiff > 6;

      var terrainMatch = board.terrain.indexOf(criteria.dominantTerrain) > -1 ? 0
        : (board.terrain.indexOf("all-mountain") > -1 ? 1 : 2);
      var skillMatch = board.skill_level === criteria.skillLevel ? 0 : (skillAdjacent(board.skill_level, criteria.skillLevel) ? 1 : 2);

      // Size-confidence term used to rank boards against each other. For a
      // weight-fit board, judge it by how well its OWN range centers the
      // customer — not by distance from the generic chart's guess, or a
      // board with a great real fit could lose to one that merely landed
      // near the chart by coincidence.
      var sizeConfidence = usedWeightFit
        ? Math.abs(criteria.weightKg - (best.weightMinKg + best.weightMaxKg) / 2)
        : lengthDiff;

      var score = terrainMatch * 30 + skillMatch * 20 + sizeConfidence + (isFallbackPool ? 500 : 0)
        + scoreJitter(criteria.sessionSeed, board.id);

      // Worst tier on either axis means this board isn't really built for
      // what the rider asked for, even if its length happens to line up —
      // surface that separately from the size-confidence badge above.
      var poorStyleFit = terrainMatch === 2 || skillMatch === 2;

      scored.push({
        board: board,
        variant: best,
        score: score,
        flags: { closestAvailable: closestAvailable, lineFallback: false, poorStyleFit: poorStyleFit, outOfStock: false }
      });
    });

    scored.sort(function (a, b) { return a.score - b.score; });
    return dedupeRecs(scored, function (r) { return r.board.id; });
  }

  function dedupeRecs(list, getId) {
    var seen = {};
    var out = [];
    list.forEach(function (rec) {
      var id = getId(rec);
      if (seen[id]) return;
      seen[id] = true;
      out.push(rec);
    });
    return out;
  }

  function skillAdjacent(a, b) {
    var order = ["beginner", "intermediate", "advanced"];
    return Math.abs(order.indexOf(a) - order.indexOf(b)) === 1;
  }

  function computeBaseLength(weightKg, heightCm, table) {
    if (!table.length) return { baseLength: 150, nudgeNote: null, row: null };

    var sorted = table.slice().sort(function (a, b) { return a.weightKg - b.weightKg; });
    var nearestIdx = 0;
    var nearestDiff = Infinity;
    sorted.forEach(function (row, i) {
      var diff = Math.abs(row.weightKg - weightKg);
      if (diff < nearestDiff) { nearestDiff = diff; nearestIdx = i; }
    });

    var primary = sorted[nearestIdx];
    var baseLength = primary.avgLengthCm;
    var nudgeNote = null;

    // Boundary tie-break: if weight sits close to the midpoint between this
    // row and the next-nearest row, let height nudge the pick by one row.
    var neighborIdx = weightKg >= primary.weightKg ? nearestIdx + 1 : nearestIdx - 1;
    var neighbor = sorted[neighborIdx];
    if (neighbor) {
      var gap = Math.abs(neighbor.weightKg - primary.weightKg);
      var distToMidpoint = Math.abs(weightKg - (primary.weightKg + neighbor.weightKg) / 2);
      var nearBoundary = gap > 0 && distToMidpoint / gap < 0.15;
      if (nearBoundary) {
        var expectedHeightForWeight = weightKg + 100;
        var tallerThanExpected = heightCm > expectedHeightForWeight;
        var neighborIsLonger = neighbor.avgLengthCm > primary.avgLengthCm;
        var shouldNudgeToNeighbor = tallerThanExpected === neighborIsLonger;
        if (shouldNudgeToNeighbor) {
          baseLength = neighbor.avgLengthCm;
          nudgeNote = "nudged " + (neighborIsLonger ? "up" : "down") + " slightly based on your height";
        }
      }
    }

    return { baseLength: baseLength, nudgeNote: nudgeNote, row: primary };
  }

  // Flags only combinations that are actually implausible (e.g. a swapped
  // height/weight typo), not just an atypical but real body type — bounded
  // by a wide BMI band (16-32) rather than a fixed ratio off a single
  // height-to-weight guess.
  function heightWeightMismatch(heightCm, weightKg) {
    var heightM = heightCm / 100;
    var minPlausible = 16 * heightM * heightM;
    var maxPlausible = 32 * heightM * heightM;
    return weightKg < minPlausible || weightKg > maxPlausible;
  }

  function terrainStyleAdjustment(terrain, skillLevel) {
    var priority = ["powder", "freeride", "park", "resort", "all-mountain"];
    var dominant = priority.filter(function (t) { return terrain.indexOf(t) > -1; })[0] || "all-mountain";

    var adj = 0;
    var notes = [];
    if (dominant === "powder") { adj += 2.5; notes.push("sized up a touch for powder float"); }
    else if (dominant === "freeride") { adj += 2.5; notes.push("sized up for stability at speed"); }
    else if (dominant === "park") { adj -= 1.5; notes.push("sized down slightly for a more playful, park-friendly feel"); }

    if (skillLevel === "beginner") { adj -= 1.5; notes.push("sized down a little to keep things manageable while you're learning"); }

    return { adj: adj, dominant: dominant, notes: notes };
  }

  // ---- Reasoning copy (boards) -------------------------------------------
  // Sentence wording is picked per board from a small set of variants,
  // seeded off the board id (not Math.random) so the same board reads the
  // same way on repeat views instead of reshuffling on every re-render —
  // but different boards shown side by side don't all read like clones.
  function hashSeed(str) {
    var h = 0;
    for (var i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
    return Math.abs(h);
  }

  function pickVariant(list, board, category) {
    return list[hashSeed(String(board.id) + "|" + category) % list.length];
  }

  // Small, session-seeded nudge added to a candidate's match score so
  // genuinely comparable products (tied or near-tied on terrain/skill/size
  // fit) rotate across different customers' sessions, instead of the sort
  // always resolving ties in fixed catalog order — which is why the same
  // 2-3 products were showing up for nearly everyone. Capped well below a
  // single terrain or skill tier gap (30 / 20) so it can only ever reorder
  // products that were already a close call, never let a worse match
  // outrank a clearly better one.
  var SCORE_JITTER_WEIGHT = 4;
  function scoreJitter(sessionSeed, id) {
    if (!sessionSeed) return 0;
    return (hashSeed(sessionSeed + "|" + id) % 1000) / 1000 * SCORE_JITTER_WEIGHT;
  }

  function buildReasoning(rec, criteria, sizeResult, styleAdj, descriptors) {
    var board = rec.board;
    var variant = rec.variant;
    var sentences = [];

    var openers = [
      "We'd put you on the {len}cm.",
      "The {len}cm is the length we'd recommend for you.",
      "You're looking at the {len}cm here.",
      "For your numbers, the {len}cm is the pick.",
      "We'd size you into the {len}cm."
    ];
    sentences.push(pickVariant(openers, board, "opener").replace("{len}", variant.lengthCm));

    // Real per-board flavor text (Liquid pulls this from the product's own
    // description — see sections/setup-builder.liquid) so cards read as
    // distinct boards rather than the same template with numbers swapped.
    if (board.description_blurb) {
      sentences.push(board.description_blurb);
    }

    var noteLeadIns = ["It's ", "That's because it's ", "Worth knowing, it's ", "Here it's "];
    var noteCount = 0;
    function pushNote(note) {
      var leadIn = noteLeadIns[hashSeed(board.id + "|note|" + noteCount) % noteLeadIns.length];
      sentences.push(leadIn + note + ".");
      noteCount++;
    }
    if (sizeResult.nudgeNote) pushNote(sizeResult.nudgeNote);
    styleAdj.notes.forEach(pushNote);

    // Describe the BOARD's own actual terrain/skill tags here, not the
    // customer's requested criteria — every recommended board shares the
    // same criteria, so using criteria values made this sentence read
    // identically across an entire results set regardless of how
    // different the boards actually are.
    var boardTerrainWords = (board.terrain || [])
      .map(function (t) { return (descriptors.terrain || {})[t]; })
      .filter(Boolean);
    var terrainWord = joinNatural(boardTerrainWords) || (descriptors.terrain || {})[criteria.dominantTerrain];
    var skillWord = (descriptors.skill_level || {})[board.skill_level] || (descriptors.skill_level || {})[criteria.skillLevel];
    if (terrainWord && skillWord) {
      // Descriptor fragments (setup-builder-data.js) are short noun/participle
      // phrases meant to slot after specific constructions like "built for X"
      // or "riders who are Y" — every variant here must keep that shape, or
      // fragments like "everything from groomers to the odd powder day" read
      // as broken grammar.
      var both = [
        "It's built for {terrain}, and suits riders who are {skill}.",
        "It's built for {terrain} — good for riders who are {skill}.",
        "This one's built for {terrain}, and works well for riders who are {skill}.",
        "It's made for {terrain}, and a solid pick for riders who are {skill}."
      ];
      sentences.push(pickVariant(both, board, "terrainskill").replace("{terrain}", terrainWord).replace("{skill}", skillWord));
    } else if (terrainWord) {
      var terrainOnly = [
        "It's built for {terrain}.",
        "This one's designed for {terrain}.",
        "Built with {terrain} riding in mind."
      ];
      sentences.push(pickVariant(terrainOnly, board, "terrainonly").replace("{terrain}", terrainWord));
    }

    if (board.shape && (descriptors.shape || {})[board.shape]) {
      sentences.push(capitalize((descriptors.shape || {})[board.shape]) + ".");
    }

    if (typeof board.flex_rating === "number") {
      var flexTemplates = [
        "Flex sits at {flex}/10.",
        "It's a {flex}/10 on the flex scale.",
        "Flex rating: {flex}/10.",
        "On flex, it comes in at {flex}/10."
      ];
      sentences.push(pickVariant(flexTemplates, board, "flex").replace("{flex}", board.flex_rating));
    }

    if (rec.flags.lineFallback) {
      var lineWord = (descriptors.product_line || {})[board.product_line] || board.product_line;
      sentences.push("This is a " + lineWord + " board — we've widened the search here because nothing in your usual range fit well.");
    } else if (rec.flags.closestAvailable) {
      sentences.push("This is the closest length we currently have in stock, rather than an exact match.");
    }

    if (rec.flags.poorStyleFit) {
      sentences.push("It's not a close match for your terrain or skill picks, but it's the best fit we have in stock right now.");
    }

    return sentences;
  }

  // ---- Helpers ----------------------------------------------------------
  function joinNatural(list) {
    if (list.length === 0) return "";
    if (list.length === 1) return list[0];
    if (list.length === 2) return list[0] + " and " + list[1];
    return list.slice(0, -1).join(", ") + ", and " + list[list.length - 1];
  }

  function isUsableBoot(boot) {
    return boot && boot.product_line && Array.isArray(boot.terrain) && Array.isArray(boot.variants) && boot.variants.length > 0;
  }

  function isUsableBoard(board) {
    return board && board.product_line && Array.isArray(board.terrain) && Array.isArray(board.variants) && board.variants.length > 0;
  }

  function isUsableBinding(binding) {
    return binding && binding.product_line && Array.isArray(binding.variants) && binding.variants.length > 0;
  }

  // ---- Matching engine: bindings -------------------------------------------
  // Boot size is a hard requirement (an ill-fitting binding is a genuine
  // safety/fit problem, not just a suboptimal pick the way a slightly-off
  // board length is) — no fallback to "closest available" the way board
  // length has one. Matches against the customer's actual picked boot/board
  // (state.picks), not the raw typed answers, so entry-system compatibility
  // and vendor-locked mounts reflect what they're actually buying.
  function matchBindings(bindings, answers, picks, cfg, excludeIds, sessionSeed) {
    excludeIds = excludeIds || [];
    function notExcluded(rec) { return excludeIds.indexOf(rec.binding.id) === -1; }

    var bootRec = picks.boot;
    var boardRec = picks.board;
    var dominantTerrain = terrainStyleAdjustment(answers.terrain, answers.skillLevel).dominant;
    var preferredLine = answers.scale === "womens" ? "womens" : "mens";
    var oppositeLine = preferredLine === "mens" ? "womens" : "mens";

    var bootSize = bootRec ? bootRec.variant.bootSizeUs : answers.bootSize;
    var bootEntrySystem = bootRec ? bootRec.boot.entry_system : null;
    var boardVendor = boardRec ? boardRec.board.vendor : null;

    function compatible(binding) {
      // Burton's EST/Channel-mount bindings only physically fit Burton's
      // own Channel system — only pair one with a Burton board.
      var isEst = /\best\b/i.test(binding.title);
      if (isEst && (!boardVendor || boardVendor.toLowerCase() !== "burton")) return false;

      // Step On boots physically require Step On bindings, and a
      // traditional-lace boot can't engage a Step On binding's mechanism —
      // this is a hard, bidirectional compatibility block, not a scoring
      // preference.
      var isStepOnBinding = /\bstep[\s-]*on\b/i.test(binding.title);
      if (bootEntrySystem === "step on" && !isStepOnBinding) return false;
      if (bootEntrySystem === "traditional" && isStepOnBinding) return false;

      return true;
    }

    var criteria = {
      bootSize: bootSize,
      dominantTerrain: dominantTerrain,
      skillLevel: answers.skillLevel,
      colourPref: answers.colourPref,
      sessionSeed: sessionSeed
    };

    var primaryPool = bindings.filter(function (b) {
      return (b.product_line === preferredLine || b.product_line === "unisex") && compatible(b);
    });
    var fallbackPool = bindings.filter(function (b) {
      return b.product_line === oppositeLine && compatible(b);
    });

    var recs = pickBindingCandidates(primaryPool, criteria, false).filter(notExcluded);
    var usedFallbackLine = false;
    if (recs.length < 2) {
      var more = pickBindingCandidates(fallbackPool, criteria, true).filter(notExcluded);
      if (more.length) usedFallbackLine = true;
      recs = dedupeRecs(recs.concat(more), function (r) { return r.binding.id; }).slice(0, 3);
    }

    var outOfStockOnly = false;
    if (!recs.length) {
      // notExcluded still applies here — see the matching comment in
      // matchBoots — otherwise a fully "Show different picks"-exhausted
      // pool comes back showing the same already-rejected bindings
      // mislabeled as out of stock instead of properly wrapping around.
      outOfStockOnly = true;
      var primaryNoStock = pickBindingCandidates(primaryPool, criteria, false, true).filter(notExcluded);
      var fallbackNoStock = pickBindingCandidates(fallbackPool, criteria, true, true).filter(notExcluded);
      recs = dedupeRecs(primaryNoStock.concat(fallbackNoStock), function (r) { return r.binding.id; }).slice(0, 3);
      recs.forEach(function (rec) { rec.flags.outOfStock = true; });
    }

    recs = recs.slice(0, 3).map(function (rec) {
      rec.flags.lineFallback = usedFallbackLine && rec.binding.product_line === oppositeLine;
      rec.reasoning = buildBindingReasoning(rec, criteria, cfg.descriptors || {});
      return rec;
    });

    var banner = null;
    if (bootEntrySystem === "step on" && !recs.length) {
      banner = "We couldn't find Step On bindings in stock for this combination — contact the store and we'll help you get sorted.";
    }

    return { recommendations: recs, banner: banner, outOfStockOnly: outOfStockOnly };
  }

  function pickBindingCandidates(pool, criteria, isFallbackPool, ignoreStock) {
    var scored = [];
    pool.forEach(function (binding) {
      var fit = binding.variants.filter(function (v) {
        return (ignoreStock || v.available) && criteria.bootSize >= v.bootMin && criteria.bootSize <= v.bootMax;
      });
      if (!fit.length) return;

      // Rank every fitting variant by how well its own range centers the
      // customer's boot size — same reasoning as the board weight-fit logic.
      var fitByCenter = fit.map(function (v) {
        var center = (v.bootMin + v.bootMax) / 2;
        return { variant: v, centerDiff: Math.abs(criteria.bootSize - center) };
      });
        fitByCenter.sort(function (a, b) { return a.centerDiff - b.centerDiff; });

      // Colour is a soft tiebreaker only, never excludes a binding outright.
      // It lives in the product TITLE, not variant.optionValues — each
      // colourway in this catalog is a separate product listing (variant
      // options only ever carry size, e.g. "M (US 8-11)"), so matching
      // against variant option text never found anything and the colour
      // preference had no effect. Every variant of one binding shares the
      // same title/colour, so this is a single per-product check, not a
      // per-variant search — no need to swap `best` between variants.
      var best = fitByCenter[0].variant;
      var hasColourPref = criteria.colourPref && criteria.colourPref !== "none";
      var colourMatch = hasColourPref && binding.title.toLowerCase().indexOf(criteria.colourPref) > -1 ? 0 : 1;

      var terrainMatch = binding.terrain.indexOf(criteria.dominantTerrain) > -1 ? 0
        : (binding.terrain.indexOf("all-mountain") > -1 ? 1 : 2);
      var skillMatch = binding.skill_level === criteria.skillLevel ? 0 : (skillAdjacent(binding.skill_level, criteria.skillLevel) ? 1 : 2);
      var bootCenterDiff = Math.abs(criteria.bootSize - (best.bootMin + best.bootMax) / 2);

      var poorStyleFit = terrainMatch === 2 || skillMatch === 2;
      var score = terrainMatch * 30 + skillMatch * 20 + colourMatch * 5 + bootCenterDiff + (isFallbackPool ? 500 : 0)
        + scoreJitter(criteria.sessionSeed, binding.id);

      scored.push({
        binding: binding,
        variant: best,
        score: score,
        flags: { lineFallback: false, poorStyleFit: poorStyleFit, outOfStock: false }
      });
    });

    scored.sort(function (a, b) { return a.score - b.score; });
    return dedupeRecs(scored, function (r) { return r.binding.id; });
  }

  function buildBindingReasoning(rec, criteria, descriptors) {
    var binding = rec.binding;
    var sentences = [];

    sentences.push(rec.variant.title + " fits your boot size.");

    if (typeof binding.flex_rating === "number") {
      sentences.push("Flex rating: " + binding.flex_rating + "/10.");
    }

    var terrainWords = (binding.terrain || [])
      .map(function (t) { return (descriptors.terrain || {})[t]; })
      .filter(Boolean);
    var terrainWord = joinNatural(terrainWords);
    var skillWord = (descriptors.skill_level || {})[binding.skill_level];
    if (terrainWord && skillWord) {
      sentences.push("Built for " + terrainWord + ", and suits riders who are " + skillWord + ".");
    } else if (terrainWord) {
      sentences.push("Built for " + terrainWord + ".");
    }

    if (rec.flags.lineFallback) {
      var lineWord = (descriptors.product_line || {})[binding.product_line] || binding.product_line;
      sentences.push("This is a " + lineWord + " binding — we've widened the search here since nothing in your usual range fit well.");
    }
    if (rec.flags.poorStyleFit) {
      sentences.push("It's not a close match for your terrain or skill picks, but it's the best fit we have for your boot size.");
    }

    return sentences;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  document.addEventListener("DOMContentLoaded", function () {
    var roots = document.querySelectorAll("[data-setup-builder]");
    roots.forEach(init);
  });
})();